import React, { useEffect, useState } from "react";
import { confirm as confirmDialog } from "../../../util/confirmDialog";
import {
    Avatar, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions,
    DialogContent, DialogTitle, IconButton,
    Paper, Stack, Tab, Tabs,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TextField, Tooltip, Typography
} from "@mui/material";
import PersonAddRoundedIcon   from "@mui/icons-material/PersonAddRounded";
import PersonRemoveRoundedIcon from "@mui/icons-material/PersonRemoveRounded";
import GroupsRoundedIcon       from "@mui/icons-material/GroupsRounded";
import ActionButton from "../../../component/common/ActionButton";
import { channelApi } from "../api/channelApi";
import { api } from "../../../util/api";
import { notify } from "../../../util/toast";
import { matchesQuery } from "../../../util/tableSearch";
import { runApiAction } from "../../../util/apiFeedback";
import { ds } from "../../../styles/designSystem";
import LoadingDots from "../../../component/common/LoadingDots";
import FormField from "../../../component/common/FormField";
import ComboBox from "../../../component/common/ComboBox";
import ResizableTable from "../../../component/common/ResizableTable";

const BASE = process.env.REACT_APP_API_REAL_URL;

/**
 * 참여자 탭.
 *
 * @param {boolean} isAdmin 채널 관리자(PM 포함) 여부. 참여자 추가·제거 UI 노출 기준.
 *   예전엔 이 prop 자체가 없어 <b>모든 참여자에게 '멤버 추가' 버튼과 행별 '제거' 버튼이 열려 있었고</b>,
 *   서버에도 권한 검사가 없어 일반 참여자가 클릭 한 번으로 PM 을 내보낼 수 있었다.
 *   서버(ChannelService)가 실제 차단을 하고, 여기서는 누를 수 없는 버튼을 감춘다.
 */
export default function TeamAssignTab({ channelId, pm, isAdmin = false, onRefresh }) {
    const [members, setMembers]       = useState([]);
    const [loading, setLoading]       = useState(true);
    const [open, setOpen]             = useState(false);
    const [addTab, setAddTab]         = useState(0); // 0=개인, 1=부서

    // 개인 선택 상태
    const [allEmployees, setAllEmployees] = useState([]);
    /** 명부 조회가 실패했는지 — 빈 표를 "직원이 없다" 로 읽지 않게 하려고 따로 둔다. */
    const [employeesError, setEmployeesError] = useState(false);
    const [search, setSearch]         = useState("");
    const [selected, setSelected]     = useState([]);
    const [adding, setAdding]         = useState(false);

    // 부서 단위 상태
    const [departments, setDepartments]   = useState([]);
    const [deptId, setDeptId]             = useState("");
    const [deptEmployees, setDeptEmployees] = useState([]);
    const [deptLoading, setDeptLoading]   = useState(false);
    const [deptSelected, setDeptSelected] = useState([]);

    const token = localStorage.getItem("auth_token");

    const loadMembers = async () => {
        setLoading(true);
        try {
            const data = await channelApi.getMembers(channelId);
            setMembers(Array.isArray(data) ? data : (data?.data || data || []));
        } catch {
            notify("멤버 불러오기 실패", "error");
        } finally {
            setLoading(false);
        }
    };

    /**
     * 참여자 후보 목록.
     *
     * <p><b>{@code /api/employees/list} 를 쓰면 안 된다.</b> 그쪽은 인사 관리용이라
     * {@code @RequirePermission(HR_EMPLOYEE)} 가 걸려 있고, 전 직원의 생년월일·전화번호·
     * 최종접속IP·보유권한까지 내려준다. 예전엔 여기서 그걸 불러 <b>일반직원에게 403</b> 이 났고,
     * catch 가 빈 배열로 삼켜 다이얼로그가 "추가할 직원이 없습니다" 로만 보였다 —
     * 채널을 직접 만든 사람(= 채널 ADMIN)조차 아무도 초대하지 못했다.
     *
     * <p>{@code /api/employees} 는 사람을 고르기 위한 명부(id·이름·사번·부서·직위)라 권한이 없다.
     * 채널이 필요한 건 딱 이만큼이다.
     */
    /**
     * 실패 사유를 <b>구분해서</b> 알린다.
     *
     * <p>예전엔 catch 가 status 를 버리고 "불러오기 실패" 한 줄만 띄웠다. 그래서 권한 때문에 403 이
     * 나던 시절에도 화면엔 "실패" 와 "추가할 직원이 없습니다" 만 보였고, 원인을 찾는 데 오래 걸렸다.
     * ({@code util/http.js} 가 status 를 살려 두는 것이 이걸 위해서다.)
     */
    const notifyLoadError = (e, what) => {
        if (e?.status === 403) {
            notify(`${what}를 볼 권한이 없습니다. 관리자에게 문의하세요.`, "error");
        } else if (e?.status) {
            notify(`${what} 불러오기 실패 (${e.status})`, "error");
        } else {
            notify(`${what} 불러오기 실패 — 서버에 연결하지 못했습니다`, "error");
        }
    };

    const loadAllEmployees = async () => {
        try {
            const data = await api.get(`${BASE}/api/employees`);
            setAllEmployees(Array.isArray(data) ? data : (data?.data || []));
            setEmployeesError(false);
        } catch (e) {
            setEmployeesError(true);
            notifyLoadError(e, "직원 목록");
        }
    };

    const loadDepartments = async () => {
        try {
            const data = await api.get(`${BASE}/api/employees/departments`);
            setDepartments(Array.isArray(data) ? data : (data?.data || []));
        } catch (e) {
            notifyLoadError(e, "부서 목록");
        }
    };

    useEffect(() => { loadMembers(); }, [channelId]);

    const handleOpenDialog = () => {
        setSearch(""); setSelected([]); setDeptId(""); setDeptEmployees([]); setDeptSelected([]);
        setEmployeesError(false);
        setAddTab(0);
        setOpen(true);
        loadAllEmployees();
        loadDepartments();
    };

    // ── 개인 선택 ──
    const handleToggle = (id) => {
        setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const handleAddSelected = () => {
        if (selected.length === 0) return;
        return runApiAction({
            request: () => channelApi.addMembers(channelId, selected),
            successMsg: `${selected.length}명이 추가되었습니다`,
            errorMsg: "멤버 추가 실패",
            setLoading: setAdding,
            onSuccess: loadMembers,
            closeOnSuccess: () => setOpen(false),
        });
    };

    // ── 부서 단위 ──
    const memberIds = new Set(members.map(m => m.employee.id));

    const handleDeptChange = async (id) => {
        setDeptId(id);
        setDeptSelected([]);
        if (!id) { setDeptEmployees([]); return; }
        setDeptLoading(true);
        try {
            // loadAllEmployees 와 같은 엔드포인트를 쓴다 — 한쪽만 관리용(/list)으로 두면
            // 개인 탭은 되는데 부서 탭만 403 으로 0명이 되는, 더 찾기 어려운 형태가 된다.
            const data = await api.get(`${BASE}/api/employees`);
            const all = Array.isArray(data) ? data : (data?.data || []);
            // 해당 부서 직원 중 아직 멤버가 아닌 사람만
            const filtered = all.filter(e => String(e.deptId) === String(id) && !memberIds.has(e.id));
            setDeptEmployees(filtered);
            setDeptSelected(filtered.map(e => e.id)); // 기본 전체 선택
        } catch (e) { notifyLoadError(e, "부서 직원"); }
        finally { setDeptLoading(false); }
    };

    const handleToggleDept = (id) => {
        setDeptSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const handleAddDept = () => {
        if (deptSelected.length === 0) return;
        return runApiAction({
            request: () => channelApi.addMembers(channelId, deptSelected),
            successMsg: `${deptSelected.length}명이 추가되었습니다`,
            errorMsg: "멤버 추가 실패",
            setLoading: setAdding,
            onSuccess: loadMembers,
            closeOnSuccess: () => setOpen(false),
        });
    };

    const handleRemove = async (employeeId) => {
        if (!(await confirmDialog({ message: "멤버를 제거하시겠습니까?" }))) return;
        return runApiAction({
            request: () => channelApi.removeMember(channelId, employeeId),
            successMsg: "멤버가 제거되었습니다",
            errorMsg: "멤버 제거 실패",
            onSuccess: loadMembers,
        });
    };

    // 공용 검색 유틸 — trim + 대소문자 무시(사번·영문 부서명 검색 일관성)
    const filtered = allEmployees.filter(e =>
        !memberIds.has(e.id) && matchesQuery(e, search, ["name", "employeeCode", "deptName"])
    );

    // 관리자를 먼저 보여준다.
    const sortedMembers = [...members].sort(
        (a, b) => (a.role === "ADMIN" ? 0 : 1) - (b.role === "ADMIN" ? 0 : 1)
    );

    return (
        <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Stack direction="row" alignItems="center" spacing={0.75}>
                    <Typography sx={ds.typography.sectionTitle}>참여자</Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: ds.colors.primary }}>
                        {members.length}
                    </Typography>
                </Stack>
                {isAdmin && (
                    <ActionButton variant="add"
                        startIcon={<PersonAddRoundedIcon />}
                        onClick={handleOpenDialog}
                        label="멤버 추가" />
                )}
            </Stack>

            {pm && (
                <Paper sx={{ p: 2, mb: 2, borderRadius: 1.5, border: `1px solid ${ds.colors.primaryBorder}`, bgcolor: ds.colors.primaryBg }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: ds.colors.primary, mb: 1 }}>PM (프로젝트 매니저)</Typography>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Avatar sx={{ width: 36, height: 36 }}>{pm.name?.[0]}</Avatar>
                        <Box>
                            <Typography sx={{ fontWeight: 700, fontSize: 14 }}>{pm.name}</Typography>
                            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{pm.deptName} · {pm.positionName}</Typography>
                        </Box>
                    </Stack>
                </Paper>
            )}

            {loading ? (
                <LoadingDots />
            ) : members.length === 0 ? (
                <Typography sx={{ py: 4, textAlign: "center", color: "text.secondary", fontSize: 13 }}>
                    등록된 멤버가 없습니다
                </Typography>
            ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5 }}>
                    <ResizableTable size="small" sx={ds.tableSx}>
                        <TableHead>
                            <TableRow>
                                <TableCell>이름</TableCell>
                                <TableCell>부서</TableCell>
                                <TableCell>직책</TableCell>
                                <TableCell>역할</TableCell>
                                {isAdmin && <TableCell sx={{ width: 48 }} align="center">관리</TableCell>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedMembers.map(m => (
                                <TableRow key={m.id} hover>
                                    <TableCell sx={{ py: 1.2 }}>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <Avatar sx={{ width: 28, height: 28, fontSize: 12 }}>
                                                {m.employee.name?.[0]}
                                            </Avatar>
                                            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{m.employee.name}</Typography>
                                        </Stack>
                                    </TableCell>
                                    <TableCell sx={{ fontSize: 13, py: 1.2, color: "text.secondary" }}>{m.employee.deptName || "-"}</TableCell>
                                    <TableCell sx={{ fontSize: 13, py: 1.2, color: "text.secondary" }}>{m.employee.positionName || "-"}</TableCell>
                                    <TableCell sx={{ py: 1.2 }}>
                                        <Chip
                                            label={m.role === "ADMIN" ? "관리자" : "멤버"}
                                            size="small"
                                            sx={{
                                                fontSize: 11, height: 20,
                                                bgcolor: m.role === "ADMIN" ? ds.colors.primaryLight : ds.colors.borderLight,
                                                color: m.role === "ADMIN" ? ds.colors.primaryText : ds.colors.textSecondary,
                                            }}
                                        />
                                    </TableCell>
                                    {isAdmin && (
                                        <TableCell align="center" sx={{ py: 1.2 }}>
                                            {/* PM 은 '채널 수정' 에서 교체한다 — 여기서 빼면 자기 채널에 못 들어오는
                                                PM 이 생기고, 다시 초대해도 역할이 되살아나지 않는다(서버도 함께 막는다). */}
                                            {m.employee.id === pm?.id ? (
                                                <Tooltip title="PM 은 '채널 수정' 에서 교체할 수 있습니다">
                                                    <span>
                                                        <IconButton size="small" disabled>
                                                            <PersonRemoveRoundedIcon sx={{ fontSize: 16 }} />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            ) : (
                                                <Tooltip title="제거">
                                                    <IconButton size="small" onClick={() => handleRemove(m.employee.id)}>
                                                        <PersonRemoveRoundedIcon sx={{ fontSize: 16 }} />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </ResizableTable>
                </TableContainer>
            )}

            {/* ── 멤버 추가 다이얼로그 ── */}
            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 700, pb: 0 }}>
                    멤버 추가
                    {(addTab === 0 ? selected : deptSelected).length > 0 && (
                        <Chip
                            label={`${(addTab === 0 ? selected : deptSelected).length}명 선택됨`}
                            size="small" color="primary" sx={{ ml: 1, fontSize: 12 }}
                        />
                    )}
                </DialogTitle>

                {/* 탭: 개인 선택 / 부서 단위 */}
                <Box sx={{ px: 3, borderBottom: "1px solid rgba(0,0,0,0.08)" }}>
                    <Tabs value={addTab} onChange={(_, v) => setAddTab(v)} sx={ds.components.tabs.root}>
                        <Tab label="개인 선택" />
                        <Tab label={<Stack direction="row" alignItems="center" spacing={0.5}><GroupsRoundedIcon sx={{ fontSize: 16 }} /><span>부서 단위</span></Stack>} />
                    </Tabs>
                </Box>

                <DialogContent sx={{ pb: 0, pt: 2 }}>
                    {/* ── 개인 선택 탭 ── */}
                    {addTab === 0 && (
                        <>
                            <TextField
                                size="small" fullWidth placeholder="이름, 사번, 부서 검색"
                                value={search} onChange={e => setSearch(e.target.value)}
                                sx={{ mb: 1 }}
                            />
                            <TableContainer sx={{ maxHeight: 340, border: "1px solid rgba(0,0,0,0.08)", borderRadius: 1 }}>
                                <ResizableTable size="small" sx={ds.tableSx}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    size="small"
                                                    indeterminate={selected.length > 0 && selected.length < filtered.length}
                                                    checked={filtered.length > 0 && filtered.every(e => selected.includes(e.id))}
                                                    onChange={() => {
                                                        const allIds = filtered.map(e => e.id);
                                                        if (filtered.every(e => selected.includes(e.id))) {
                                                            setSelected(prev => prev.filter(id => !allIds.includes(id)));
                                                        } else {
                                                            setSelected(prev => [...new Set([...prev, ...allIds])]);
                                                        }
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>이름</TableCell>
                                            <TableCell>부서</TableCell>
                                            <TableCell>직책</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {filtered.map(e => (
                                            <TableRow key={e.id} hover selected={selected.includes(e.id)}
                                                onClick={() => handleToggle(e.id)} sx={{ cursor: "pointer" }}>
                                                <TableCell padding="checkbox">
                                                    <Checkbox size="small" checked={selected.includes(e.id)} />
                                                </TableCell>
                                                <TableCell>
                                                    <Stack direction="row" alignItems="center" spacing={1}>
                                                        <Avatar sx={{ width: 26, height: 26, fontSize: 11, bgcolor: "#e3e8f0", color: "#333" }}>
                                                            {e.name?.[0]}
                                                        </Avatar>
                                                        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{e.name}</Typography>
                                                    </Stack>
                                                </TableCell>
                                                <TableCell sx={{ fontSize: 13, color: "text.secondary" }}>{e.deptName || "-"}</TableCell>
                                                <TableCell sx={{ fontSize: 13, color: "text.secondary" }}>{e.positionName || "-"}</TableCell>
                                            </TableRow>
                                        ))}
                                        {filtered.length === 0 && (
                                            <TableRow>
                                                {/* 조회 실패를 "직원이 없다" 로 보여주면 안 된다 — 예전에 403 이
                                                    이 문구로 위장돼 원인을 찾는 데 오래 걸렸다. */}
                                                <TableCell colSpan={4} align="center" sx={{ py: 3, color: "text.secondary", fontSize: 13 }}>
                                                    {employeesError
                                                        ? "직원 목록을 불러오지 못했습니다. 다시 시도해 주세요."
                                                        : "추가할 직원이 없습니다"}
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </ResizableTable>
                            </TableContainer>
                        </>
                    )}

                    {/* ── 부서 단위 탭 ── */}
                    {addTab === 1 && (
                        <Box>
                            <FormField label="부서 선택" sx={{ mb: 2 }}>
                                <ComboBox placeholder="선택하세요"
                                    options={departments.map(d => ({ value: d.deptId, label: d.deptName }))}
                                    value={deptId}
                                    onChange={v => handleDeptChange(v)} />
                            </FormField>

                            {deptLoading ? (
                                <LoadingDots compact />
                            ) : deptId && deptEmployees.length === 0 ? (
                                <Typography sx={{ textAlign: "center", color: "text.secondary", fontSize: 13, py: 2 }}>
                                    추가 가능한 직원이 없습니다 (이미 모두 멤버입니다)
                                </Typography>
                            ) : deptEmployees.length > 0 ? (
                                <>
                                    <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                                        <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                                            {deptEmployees.length}명 중 {deptSelected.length}명 선택됨
                                        </Typography>
                                        <ActionButton variant="secondary"
                                            startIcon={false}
                                            onClick={() => {
                                                if (deptSelected.length === deptEmployees.length) setDeptSelected([]);
                                                else setDeptSelected(deptEmployees.map(e => e.id));
                                            }}
                                            sx={{ fontSize: 12, borderColor: "transparent",
                                                "&:hover": { borderColor: "transparent", bgcolor: "transparent" } }}
                                            label={deptSelected.length === deptEmployees.length ? "전체 해제" : "전체 선택"} />
                                    </Stack>
                                    <TableContainer sx={{ maxHeight: 280, border: "1px solid rgba(0,0,0,0.08)", borderRadius: 1 }}>
                                        <ResizableTable size="small" sx={ds.tableSx}>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell padding="checkbox" />
                                                    <TableCell>이름</TableCell>
                                                    <TableCell>직책</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {deptEmployees.map(e => (
                                                    <TableRow key={e.id} hover selected={deptSelected.includes(e.id)}
                                                        onClick={() => handleToggleDept(e.id)} sx={{ cursor: "pointer" }}>
                                                        <TableCell padding="checkbox">
                                                            <Checkbox size="small" checked={deptSelected.includes(e.id)} />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                                <Avatar sx={{ width: 26, height: 26, fontSize: 11, bgcolor: "#e3e8f0", color: "#333" }}>
                                                                    {e.name?.[0]}
                                                                </Avatar>
                                                                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{e.name}</Typography>
                                                            </Stack>
                                                        </TableCell>
                                                        <TableCell sx={{ fontSize: 13, color: "text.secondary" }}>{e.positionName || "-"}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </ResizableTable>
                                    </TableContainer>
                                </>
                            ) : (
                                <Typography sx={{ textAlign: "center", color: "text.secondary", fontSize: 13, py: 4 }}>
                                    부서를 선택하면 해당 부서 직원 목록이 표시됩니다
                                </Typography>
                            )}
                        </Box>
                    )}
                </DialogContent>

                <DialogActions sx={{ px: 3, py: 2 }}>
                    <ActionButton variant="cancel" onClick={() => setOpen(false)} />
                    {addTab === 0 ? (
                        <ActionButton variant="primary"
                            onClick={handleAddSelected}
                            disabled={selected.length === 0}
                            loading={adding}
                            label={selected.length > 0 ? `${selected.length}명 추가` : "추가"} />
                    ) : (
                        <ActionButton variant="primary"
                            onClick={handleAddDept}
                            disabled={deptSelected.length === 0}
                            loading={adding}
                            label={deptSelected.length > 0 ? `${deptSelected.length}명 추가` : "추가"} />
                    )}
                </DialogActions>
            </Dialog>
        </Box>
    );
}
